import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram, ArrowUpRight } from "lucide-react";
import { useLang, LANGS } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";

const IG = "barbarauwadiee";

export default function SideNav({ open, onClose }) {
  const { t, lang, setLang } = useLang();
  const { user, login, logout } = useAuth();

  const items = [
    { to: "/", label: t("nav_home") },
    { to: "/su-di-me", label: t("nav_about") },
    { to: "/writing", label: t("nav_writing") },
    { to: "/video", label: t("nav_video") },
    { to: "/short", label: t("nav_short") },
    { to: "/audio", label: t("nav_audio") },
    { to: "/premium", label: t("nav_premium") },
    { to: "/shop", label: t("nav_shop") },
    { to: "/corsi", label: t("nav_courses") },
    ...(user ? [{ to: "/area", label: "Area personale" }] : []),
    { to: "/contact", label: t("nav_contact") },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }} onClick={onClose}
            data-testid="side-nav-overlay"
          />
          <motion.aside
            className="fixed top-0 left-0 z-50 h-full w-[86%] max-w-[420px] bg-[#FFFFFF] border-r border-[rgba(198,137,158,0.2)] flex flex-col"
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="side-nav-drawer"
          >
            <div className="flex items-center justify-between px-8 h-20 border-b border-[rgba(61,50,56,0.08)]">
              <span className="font-serif text-xl tracking-[0.18em]">UNFILTERED</span>
              <button data-testid="side-nav-close" onClick={onClose} className="text-[#8C7E83] hover:text-[#C6899E] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-8 py-8">
              <ul className="space-y-1">
                {items.map((it, i) => (
                  <motion.li key={it.to}
                    initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.04, duration: 0.4 }}>
                    <Link to={it.to} onClick={onClose}
                      data-testid={`nav-link-${it.to === "/" ? "home" : it.to.replace("/", "")}`}
                      className="group flex items-center justify-between py-2.5 font-serif text-2xl text-[#3D3238] hover:text-[#C6899E] transition-colors">
                      <span>{it.label}</span>
                      <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#C6899E]" />
                    </Link>
                  </motion.li>
                ))}
              </ul>

              <a href={`https://instagram.com/${IG}`} target="_blank" rel="noreferrer"
                data-testid="nav-link-instagram"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#8C7E83] hover:text-[#C6899E] transition-colors">
                <Instagram className="w-4 h-4" /> @{IG}
              </a>
            </nav>

            <div className="px-8 py-6 border-t border-[rgba(61,50,56,0.08)] space-y-5">
              <div className="flex items-center gap-2" data-testid="lang-switcher">
                {LANGS.map((l) => (
                  <button key={l} onClick={() => setLang(l)}
                    data-testid={`lang-${l}`}
                    className={`text-xs uppercase tracking-[0.15em] px-2 py-1 transition-colors ${lang === l ? "text-[#C6899E] border-b border-[#C6899E]" : "text-[#9C8F94] hover:text-[#3D3238]"}`}>
                    {l}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between">
                {user ? (
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />}
                      <span className="text-xs text-[#8C7E83] truncate">{user.name || user.email}</span>
                    </div>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={onClose} data-testid="nav-link-admin"
                        className="text-xs uppercase tracking-[0.15em] text-[#C6899E] hover:text-[#B4718C]">{t("admin")}</Link>
                    )}
                    <button onClick={() => { logout(); onClose(); }} data-testid="logout-btn"
                      className="text-xs uppercase tracking-[0.15em] text-[#9C8F94] hover:text-[#3D3238]">{t("logout")}</button>
                  </div>
                ) : (
                  <button onClick={login} data-testid="login-btn"
                    className="text-xs uppercase tracking-[0.2em] text-[#C6899E] hover:text-[#B4718C] border border-[rgba(198,137,158,0.3)] px-5 py-2.5 rounded-full transition-colors w-full">
                    {t("login")}
                  </button>
                )}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
